import { useCADPageStore, WAND } from '@/state'
import { Camera, Canvas } from '@react-three/fiber'
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { COLOR_OPTIONS } from '@/constants'

import { Scene } from '@/pages/CADPage/components/CADScene/CADScene'
import { eventManagerFactory } from '@/pages/CADPage/components/CADModel/hooks/useCanvasEventManager'
import { useCADQuery } from '@/services/queries/cads'
import { GLTFObject } from './GLTFObject'

const isWebGLContextError = (error: Error) =>
  error.message.includes('Error creating WebGL context') ||
  error.message.includes('WebGL context')

const WebGLFallback = () => (
  <div className="w-full h-full flex items-center justify-center rounded-md border bg-gray-50 p-4 text-sm text-gray-700">
    <div>
      <h1 className="text-lg mb-1">Could not initialize WebGL.</h1>
      <p className="mb-1">
        Make sure WebGL is enabled, or you might be using an unsupported
        configuration.
      </p>
      <p>
        1. Check &quot;Use Hardware Acceleration when available.&quot; under
        Chrome &gt; Settings &gt; Advanced Settings
      </p>
      <p>2. Restart your browser.</p>
      <p className="mt-1">
        Check the help pages{' '}
        <a
          href="https://q20help.notion.site/Quarter20-Spec-and-Computer-Requirements-2727d5cb96da809a894bcc9c50575de4"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-50"
        >
          here
        </a>
        .
      </p>
    </div>
  </div>
)

type CanvasContextType = {
  gltf: GLTFObject | undefined
  canvasID: string | null
  setCanvasID: (id: string | null) => void
  setGLTF: (gltf: GLTFObject | undefined) => void
  setCanvasBounds: (
    bounds: { x: number; y: number; width: number; height: number } | null,
  ) => void
  setCanvasVisible: (visible: boolean) => void
}

export const CanvasContext = createContext<CanvasContextType>({
  gltf: undefined,
  canvasID: null,
  setCanvasID: () => {},
  setGLTF: () => {},
  setCanvasBounds: () => {},
  setCanvasVisible: () => {},
})

export const CanvasCameraContext = createContext<Camera | null>(null)

export const useCanvasContext = () => useContext(CanvasContext)
export const useCanvasCamera = () => useContext(CanvasCameraContext)

export const CanvasProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useCADQuery()
  const cadVersionId = data?.version?.id

  const [canvasBounds, setCanvasBounds] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)
  const [canvasVisible, setCanvasVisible] = useState(false)
  const [hasMountedCanvas, setHasMountedCanvas] = useState(false)
  const [camera, setCamera] = useState<Camera | null>(null)
  const [canvasID, setCanvasID] = useState<string | null>(null)
  const wandSelected = useCADPageStore((state) => state.wandSelected)

  const gltfRef = useRef<GLTFObject | undefined>(undefined)

  const setGLTF = useCallback((gltf: GLTFObject | undefined) => {
    if (gltfRef.current && gltfRef.current !== gltf) {
      gltfRef.current.dispose()
    }
    gltfRef.current = gltf
  }, [])

  // Invalidate cache when cadVersionId changes
  useEffect(() => {
    if (cadVersionId && canvasID && cadVersionId !== canvasID) {
      if (gltfRef.current) {
        gltfRef.current.dispose()
        gltfRef.current = undefined
      }
      setCanvasID(cadVersionId)
    } else if (cadVersionId && !canvasID) {
      setCanvasID(cadVersionId)
    }
  }, [cadVersionId, canvasID])

  useEffect(() => {
    return () => {
      if (gltfRef.current) {
        gltfRef.current.dispose()
        gltfRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    if (canvasVisible && canvasBounds) {
      setHasMountedCanvas(true)
    }
  }, [canvasVisible, canvasBounds])

  const contextValue = useMemo(
    () => ({
      get gltf() {
        return gltfRef.current
      },
      setCanvasBounds,
      setCanvasVisible,
      setGLTF,
      canvasID,
      setCanvasID,
    }),
    [setGLTF, canvasID],
  )

  const currentColorName = useMemo(() => {
    if (wandSelected === WAND.TRANSPARENCY) {
      return WAND.TRANSPARENCY
    }

    if (wandSelected) {
      const selectedColor = COLOR_OPTIONS.find(
        (color) => color.value === parseInt(wandSelected.slice(1), 16),
      )
      return selectedColor?.name
    }
  }, [wandSelected])

  return (
    <CanvasContext.Provider value={contextValue}>
      <div
        style={{
          position: 'fixed',
          left: canvasBounds?.x ?? 0,
          top: canvasBounds?.y ?? 0,
          width: canvasBounds?.width ?? 0,
          height: canvasBounds?.height ?? 0,
          visibility: canvasVisible && canvasBounds ? 'visible' : 'hidden',
          pointerEvents: canvasVisible && canvasBounds ? 'auto' : 'none',
          zIndex: 10,
        }}
      >
        {hasMountedCanvas && (
          <ErrorBoundary
            fallbackRender={({ error }) => {
              if (error instanceof Error && isWebGLContextError(error)) {
                return canvasVisible ? <WebGLFallback /> : null
              }

              throw error
            }}
          >
            <Canvas
              id="cad-canvas"
              data-testid="cad-page-canvas"
              events={eventManagerFactory}
              className={
                currentColorName ? `cursor-wand__${currentColorName}` : ''
              }
              fallback={<div>Loading...</div>}
              style={{
                width: '100%',
                height: '100%',
              }}
              gl={{
                toneMappingExposure: 0.8,
                preserveDrawingBuffer: true,
                localClippingEnabled: true,
                antialias: true,
                stencil: true,
              }}
              frameloop={canvasVisible ? 'demand' : 'never'}
              performance={{ min: 0.5 }}
              dpr={[1, 2]}
              onPointerDown={() =>
                useCADPageStore.setState({ isRotating: true })
              }
              onPointerUp={() =>
                useCADPageStore.setState({ isRotating: false })
              }
            >
              <Scene onCameraRef={setCamera} />
            </Canvas>
          </ErrorBoundary>
        )}
      </div>
      <CanvasCameraContext.Provider value={camera}>
        {children}
      </CanvasCameraContext.Provider>
    </CanvasContext.Provider>
  )
}

export default CanvasProvider
